import { useLocation } from 'react-router-dom'
import { LoaderCircle, TriangleAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { ImageGenerationDraftSession } from '@/features/image-generation'
import { useDefaultTopUpOfferCode } from '@/features/pricing/useRuntimePricing'
import { useSubscriptionStore } from '@/stores/subscriptionStore'

type ImagePreviewApprovalDialogProps = {
  approving?: boolean
  onApprove: () => Promise<void> | void
  onOpenChange: (open: boolean) => void
  onRegenerate: () => Promise<void> | void
  onReject: () => void
  open: boolean
  session: ImageGenerationDraftSession
}

function getTargetLabel(
  session: ImageGenerationDraftSession,
  t: (key: string, options?: Record<string, unknown>) => string,
) {
  switch (session.sourceKind) {
    case 'chapter':
      return t('imageGeneration.targets.chapter')
    case 'side_story':
      return t('imageGeneration.targets.sideStory')
    case 'timeline_event':
      return t('imageGeneration.targets.timelineEvent')
    case 'lore_entry':
      return t('imageGeneration.targets.lore')
    case 'entity_card':
      return t('imageGeneration.targets.entity')
  }
}

export function ImagePreviewApprovalDialog({
  approving = false,
  onApprove,
  onOpenChange,
  onRegenerate,
  onReject,
  open,
  session,
}: ImagePreviewApprovalDialogProps) {
  const { t } = useTranslation()
  const location = useLocation()
  const checkout = useSubscriptionStore((state) => state.checkout)
  const defaultTopUpOfferCode = useDefaultTopUpOfferCode()
  const busy = approving || session.requestStatus === 'loading'
  const creditsRemaining = session.creditsRemaining ?? session.accessState.creditsRemaining
  const targetLabel = getTargetLabel(session, t)
  const busyMessage = approving
    ? t('imageGeneration.previewDialog.approvingBody', { target: targetLabel })
    : session.requestStatus === 'loading'
      ? t('imageGeneration.previewDialog.regeneratingBody')
      : null
  const aiGuardNotices = [
    session.accessState.cacheStatus === 'hit'
      ? t('story.ai.access.cacheHit')
      : null,
    session.accessState.truncated
      ? t('story.ai.access.truncationWarning')
      : null,
  ].filter((notice): notice is string => Boolean(notice))

  function handleAccessAction() {
    if (session.accessState.code === 'tier-locked') {
      void checkout(
        session.accessState.recommendedTier === 'author' ? 'author_monthly' : 'crafter_monthly',
        location.pathname,
      )
      return
    }

    if (session.accessState.code === 'insufficient-credits') {
      void checkout(defaultTopUpOfferCode, location.pathname)
    }
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="w-[min(96vw,68rem)] gap-6">
        <DialogHeader>
          <div className="flex flex-wrap items-start justify-between gap-3 pr-10">
            <div className="space-y-2">
              <DialogTitle>
                {t('imageGeneration.previewDialog.title', { target: targetLabel })}
              </DialogTitle>
              <DialogDescription>
                {t('imageGeneration.previewDialog.description')}
              </DialogDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline">{session.providerLabel}</Badge>
              <Badge variant="outline">{session.modelName}</Badge>
              <Badge variant="outline">{targetLabel}</Badge>
              <Badge variant="outline">
                {t('imageGeneration.promptDialog.credits', { count: session.creditCost })}
              </Badge>
              {typeof creditsRemaining === 'number' ? (
                <Badge variant="outline">
                  {t('imageGeneration.promptDialog.remaining', { count: creditsRemaining })}
                </Badge>
              ) : null}
            </div>
          </div>
        </DialogHeader>

        <div className="grid gap-5 xl:grid-cols-[minmax(0,1.15fr)_minmax(18rem,0.85fr)]">
          <div className="overflow-hidden rounded-[28px] border border-white/70 bg-white/80 p-4 shadow-[0_22px_70px_-34px_rgba(40,31,19,0.44)]">
            {session.previewDataUrl ? (
              <img
                alt={`${session.sourceTitle} preview`}
                className="h-[28rem] w-full rounded-[22px] object-cover"
                src={session.previewDataUrl}
              />
            ) : (
              <div className="flex h-[28rem] items-center justify-center rounded-[22px] border border-dashed border-border bg-secondary/20 px-6 text-center text-sm text-muted-foreground">
                {t('imageGeneration.previewDialog.noPreview')}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <section className="rounded-[26px] border border-border/70 bg-secondary/25 p-4">
              <div className="space-y-1">
                <p className="text-sm font-semibold text-foreground">
                  {t('imageGeneration.previewDialog.saveTargetTitle')}
                </p>
                <p className="text-sm text-muted-foreground">{session.sourceTitle}</p>
              </div>
              <p className="mt-3 text-sm leading-6 text-muted-foreground">
                {session.existingImagePath
                  ? t('imageGeneration.previewDialog.replaceBody', { target: targetLabel })
                  : t('imageGeneration.previewDialog.saveBody', { target: targetLabel })}
              </p>
            </section>

            <section className="rounded-[26px] border border-border/70 bg-white/80 p-4">
              <p className="text-sm font-semibold text-foreground">
                {t('imageGeneration.previewDialog.promptUsedTitle')}
              </p>
              <p className="mt-2 text-sm leading-6 text-muted-foreground whitespace-pre-wrap">
                {session.editedPrompt}
              </p>
            </section>

            {aiGuardNotices.length > 0 ? (
              <div className="space-y-2 rounded-2xl border border-sky-300/35 bg-sky-50/70 px-4 py-3 text-sm text-sky-950">
                {aiGuardNotices.map((notice) => (
                  <p key={notice}>{notice}</p>
                ))}
              </div>
            ) : null}

            {busyMessage ? (
              <div
                className="flex items-start gap-3 rounded-2xl border border-border/70 bg-secondary/35 px-4 py-3 text-sm text-muted-foreground"
                role="status"
              >
                <LoaderCircle className="mt-0.5 h-4 w-4 animate-spin" />
                <p>{busyMessage}</p>
              </div>
            ) : session.errorMessage ? (
              <div
                className="flex items-start gap-3 rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive"
                role="alert"
              >
                <TriangleAlert className="mt-0.5 h-4 w-4" />
                <div className="space-y-1">
                  <p className="font-semibold">
                    {t('imageGeneration.previewDialog.errorTitle')}
                  </p>
                  <p>{session.errorMessage}</p>
                  <p className="text-destructive/80">
                    {t('imageGeneration.previewDialog.errorRecovery')}
                  </p>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <DialogFooter>
          {session.accessState.code === 'tier-locked' ? (
            <Button disabled={busy} onClick={handleAccessAction} type="button" variant="outline">
              {t('imageGeneration.actions.upgradePlan')}
            </Button>
          ) : null}
          {session.accessState.code === 'insufficient-credits' ? (
            <Button disabled={busy} onClick={handleAccessAction} type="button" variant="outline">
              {t('imageGeneration.actions.buyCredits')}
            </Button>
          ) : null}
          <Button onClick={() => onOpenChange(false)} type="button" variant="ghost">
            {t('common.cancel')}
          </Button>
          <Button disabled={busy} onClick={onReject} type="button" variant="outline">
            {t('imageGeneration.actions.rejectPreview')}
          </Button>
          <Button
            disabled={busy}
            onClick={() => void onRegenerate()}
            type="button"
            variant="outline"
          >
            {session.requestStatus === 'loading'
              ? t('imageGeneration.actions.regenerating')
              : t('imageGeneration.actions.regenerate')}
          </Button>
          <Button disabled={busy || !session.previewDataUrl} onClick={() => void onApprove()} type="button">
            {approving
              ? t('imageGeneration.actions.savingTarget', { target: targetLabel })
              : t('imageGeneration.actions.approveTarget', { target: targetLabel })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
